import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { User, Mail } from 'lucide-react';
import { Button } from '@shared/components/ui/button';
import { Checkbox } from '@shared/components/ui/checkbox';
import { BookingBreadcrumbs } from '@features/booking/components/BookingBreadcrumbs';
import { BookingPageLayout } from '@features/booking/layouts';
import { useBookingFlow } from '@features/booking/flow';
import { useBooking } from '@features/booking/state';
import { isPaymentGuaranteeType } from '@features/booking/state/types';
import { useRestaurant } from '@core/tenant/TenantProvider';
import { PhoneInput } from '@shared/components/common/PhoneInput';
import { Private } from '@shared/components/common/Private';
import { isValidPhone } from '@shared/utils/phoneValidation';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const AgentGuestInfoPage = () => {
  const { t } = useTranslation('booking');
  const restaurant = useRestaurant();
  const { goToNext } = useBookingFlow();
  const booking = useBooking();

  const [name, setName] = useState(booking.guestName || '');
  const [email, setEmail] = useState(booking.guestEmail || '');
  const [phone, setPhone] = useState(booking.guestPhone || '');
  const [requestGuarantee, setRequestGuarantee] = useState(booking.requestGuarantee);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const slotRequiresGuarantee = isPaymentGuaranteeType(booking.guaranteeType);

  const handleSubmit = () => {
    const newErrors: Record<string, string> = {};

    if (!name.trim()) {
      newErrors.name = t('AGENT_FORM_ERROR_TYPE_NAME');
    }

    if (!email.trim() || !EMAIL_REGEX.test(email.trim())) {
      newErrors.email = t('AGENT_GUEST_EMAIL_REQUIRED');
    }

    if (!isValidPhone(phone)) {
      newErrors.phone = t('AGENT_GUEST_PHONE_REQUIRED');
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});
    booking.updateBooking({
      guestName: name.trim(),
      guestEmail: email.trim(),
      guestPhone: phone.trim(),
      requestGuarantee,
    });

    goToNext();
  };

  return (
    <BookingPageLayout
      title={t('AGENT_TITLE_1')}
      subtitle={t('AGENT_TITLE_2')}
      breadcrumbs={<BookingBreadcrumbs />}
    >
      {/* Agent collects guest name/email/phone — all PII. Wrap in <Private>
          so session-replay tools don't record what restaurant staff type
          into the booking flow on behalf of guests. */}
      <Private className="w-full px-6 space-y-5">
        {/* Name */}
        <div className="space-y-1.5">
          <label htmlFor="agent-guest-name" className="text-sm font-medium text-foreground/70">
            {t('AGENT_GUEST_NAME')}
          </label>
          <div className="relative">
            <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-foreground/40" />
            <input
              id="agent-guest-name"
              type="text"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (errors.name) setErrors((prev) => ({ ...prev, name: '' }));
              }}
              placeholder={t('AGENT_GUEST_NAME_PLACEHOLDER')}
              className={`w-full rounded-xl border pl-10 pr-3 py-3 text-foreground focus:border-brand focus:outline-none transition-colors ${
                errors.name ? 'border-red-400' : 'border-border'
              }`}
            />
          </div>
          {errors.name && <p className="text-red-500 text-xs">{errors.name}</p>}
        </div>

        {/* Email */}
        <div className="space-y-1.5">
          <label htmlFor="agent-guest-email" className="text-sm font-medium text-foreground/70">
            {t('AGENT_GUEST_EMAIL')}
          </label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-foreground/40" />
            <input
              id="agent-guest-email"
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (errors.email) setErrors((prev) => ({ ...prev, email: '' }));
              }}
              placeholder={t('AGENT_GUEST_EMAIL_PLACEHOLDER')}
              className={`w-full rounded-xl border pl-10 pr-3 py-3 text-foreground focus:border-brand focus:outline-none transition-colors ${
                errors.email ? 'border-red-400' : 'border-border'
              }`}
            />
          </div>
          {errors.email && <p className="text-red-500 text-xs">{errors.email}</p>}
        </div>

        {/* Phone */}
        <div className="space-y-1.5">
          <label htmlFor="agent-guest-phone" className="text-sm font-medium text-foreground/70">
            {t('AGENT_GUEST_PHONE')}
          </label>
          <PhoneInput
            value={phone}
            onChange={(val) => {
              setPhone(val);
              if (errors.phone) setErrors((prev) => ({ ...prev, phone: '' }));
            }}
            defaultCountry={restaurant.isoCountryCode}
            dialCode={restaurant.countryCode}
            className={`rounded-xl border px-3 py-3 focus-within:border-brand transition-colors ${
              errors.phone ? 'border-red-400' : 'border-border'
            }`}
          />
          {errors.phone && <p className="text-red-500 text-xs">{errors.phone}</p>}
        </div>

        {/* Request guarantee checkbox */}
        {slotRequiresGuarantee && (
          <div className="flex items-start gap-3 pt-2">
            <Checkbox
              id="request-guarantee"
              checked={requestGuarantee}
              onCheckedChange={(checked) => setRequestGuarantee(checked === true)}
              className="mt-0.5"
            />
            <label htmlFor="request-guarantee" className="text-sm text-foreground/70 cursor-pointer">
              {t('ASK_TO_SEND_REQUEST_GUARANTEE_EMAIL')}
            </label>
          </div>
        )}
      </Private>

      <div className="w-full px-6 mt-12">
        <Button
          className="bg-brand text-brand-foreground font-medium py-6 px-8 rounded-xl w-full hover:bg-brand-dark shadow-md"
          onClick={handleSubmit}
        >
          {t('NEW_MODAL_CONTINUE')}
        </Button>
      </div>
    </BookingPageLayout>
  );
};

export default AgentGuestInfoPage;
